// src/pages/InitialInventoryPage.tsx
// Initial Inventory Setup page for qmLEO
// Allows OrgAdmin+ to add existing inventory without purchase orders
// Used during agency onboarding or when finding items post-go-live

import React, { useState, useEffect, useCallback } from 'react';
import { Package, Plus, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { productCatalogAPI, Product } from '../services/productCatalogAPI';
import { categoryAPI } from '../services/categoryAPI';
import { initialInventoryAPI, SessionSummaryResponse } from '../services/initialInventoryAPI';
import { CategoryHierarchy } from '../types/category';
import { getItemTrackingConfig } from '../utils/categoryTracking';
import ProductSearchCreate from '../components/initial-inventory/ProductSearchCreate';
import SerializedItemEntry from '../components/initial-inventory/SerializedItemEntry';
import BulkQuantityEntry from '../components/initial-inventory/BulkQuantityEntry';
import SessionSummary from '../components/initial-inventory/SessionSummary';
import AddProductModal from '../components/product/AddProductModal';
import { logger } from '../utils/logger';

const pageLogger = logger.createServiceLogger('InitialInventoryPage');

// Session storage key and expiry (24 hours)
const SESSION_KEY = 'qm_initial_inventory_session';
const SESSION_EXPIRY_MS = 24 * 60 * 60 * 1000;

interface SessionData {
  startedAt: string;
  expiresAt: string;
}

interface BodyArmorData {
  ballisticLevel: string;
  size: string;
  expirationDate: string;
}

const InitialInventoryPage: React.FC = () => {
  // Data state
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<CategoryHierarchy[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [sessionSummary, setSessionSummary] = useState<SessionSummaryResponse | null>(null);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Session tracking
  const [sessionStart, setSessionStart] = useState<string | null>(null);

  // Initialize session and load data
  useEffect(() => {
    initializeSession();
    loadData();
  }, []);

  // Load session summary when session starts
  useEffect(() => {
    if (sessionStart) {
      loadSessionSummary();
    }
  }, [sessionStart]);

  const initializeSession = () => {
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      
      if (stored) {
        const session: SessionData = JSON.parse(stored);
        const now = new Date().getTime();
        const expiry = new Date(session.expiresAt).getTime();
        
        if (now < expiry) {
          // Session still valid
          setSessionStart(session.startedAt);
          pageLogger.info('Restored existing session', { startedAt: session.startedAt });
          return;
        }
        
        // Session expired, remove it
        localStorage.removeItem(SESSION_KEY);
        pageLogger.info('Previous session expired, starting new one');
      }
      
      // Start new session
      const now = new Date();
      const newSession: SessionData = {
        startedAt: now.toISOString(),
        expiresAt: new Date(now.getTime() + SESSION_EXPIRY_MS).toISOString()
      };
      
      localStorage.setItem(SESSION_KEY, JSON.stringify(newSession));
      setSessionStart(newSession.startedAt);
      pageLogger.info('Started new session', { startedAt: newSession.startedAt });
      
    } catch (err) {
      pageLogger.error('Failed to initialize session', undefined, err as Error);
      // Start fresh session on error
      const now = new Date().toISOString();
      setSessionStart(now);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load categories
      const categoryResponse = await categoryAPI.getAllCategories();
      if (categoryResponse.success && categoryResponse.data) {
        setCategories(categoryResponse.data.categories);
        pageLogger.debug('Loaded categories', { count: categoryResponse.data.categories.length });
      }

      // Load active products
      const productList = await productCatalogAPI.listProducts({ active: true });
      setProducts(productList);
      pageLogger.debug('Loaded products', { count: productList.length });

    } catch (err: any) {
      pageLogger.error('Failed to load data', undefined, err);
      setError('Failed to load data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const loadSessionSummary = useCallback(async () => {
    if (!sessionStart) return;
    
    try {
      const summary = await initialInventoryAPI.getSessionSummary(sessionStart);
      setSessionSummary(summary);
      pageLogger.debug('Session summary loaded', { 
        totalItems: summary.totalItems,
        totalQuantity: summary.totalQuantity 
      });
    } catch (err) {
      pageLogger.error('Failed to load session summary', undefined, err as Error);
      // Don't show error to user - session summary is non-critical
    }
  }, [sessionStart]);

  const handleProductSelected = (product: Product | null) => {
    setSelectedProduct(product);
    setSuccessMessage(null);
    setError(null);
    
    if (product) {
      pageLogger.info('Product selected', { 
        productId: product.productId, 
        name: product.name,
        trackingType: product.trackingType || product.productType 
      });
    }
  };

  const handleCreateProduct = () => {
    setShowAddProductModal(true);
  };

  const handleProductCreated = (newProduct: Product) => {
    setProducts([newProduct, ...products]);
    setSelectedProduct(newProduct);
    setShowAddProductModal(false);
    pageLogger.info('New product created and selected', { 
      productId: newProduct.productId, 
      name: newProduct.name 
    });
  };

  // Helper to find parent category ID from a subcategory
  const findParentCategoryId = (subcategoryId: string): string => {
    const subcategory = categories.find(c => c.categoryId === subcategoryId);
    if (subcategory?.parentCategoryId) {
      return subcategory.parentCategoryId;
    }
    // If no parent found, return the original ID (it might be a top-level category)
    return subcategoryId;
  };

  const handleSerializedSubmit = async (
    serialNumbers: string[],
    location: string,
    condition: string,
    notes?: string,
    bodyArmor?: BodyArmorData
  ) => {
    if (!selectedProduct) return;

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage(null);

      // Resolve parent category - product.categoryId is actually the subcategory
      const subcategoryId = selectedProduct.subcategoryId || selectedProduct.categoryId;
      const parentCategoryId = findParentCategoryId(subcategoryId);

      // Build items array with body armor data if provided
      const items = serialNumbers.map(sn => ({
        serialNumber: sn,
        ...(bodyArmor && { bodyArmor })
      }));

      const result = await initialInventoryAPI.createSerializedItems({
        productId: selectedProduct.productId,
        categoryId: parentCategoryId,
        subcategoryId: subcategoryId,
        items,
        defaultLocation: location,
        defaultCondition: condition,
        acquisitionNotes: notes,
        acquisitionSource: 'INITIAL_INVENTORY'
      });

      pageLogger.info('Serialized items created', { 
        productId: selectedProduct.productId,
        itemsCreated: result.itemsCreated,
        itemsRequested: result.itemsRequested,
        hasBodyArmor: !!bodyArmor
      });

      // Show success message
      if (result.partialSuccess) {
        setSuccessMessage(`Added ${result.itemsCreated} of ${result.itemsRequested} items. Some items had errors.`);
      } else {
        setSuccessMessage(`Successfully added ${result.itemsCreated} ${selectedProduct.name} items to inventory.`);
      }

      // Refresh session summary
      await loadSessionSummary();

    } catch (err: any) {
      pageLogger.error('Failed to create serialized items', undefined, err);
      setError(err.message || 'Failed to add items. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkSubmit = async (
    quantity: number,
    location: string,
    subLocation?: string,
    lotNumber?: string,
    expirationDate?: string,
    notes?: string
  ) => {
    if (!selectedProduct) return;

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage(null);

      // Resolve parent category - product.categoryId is actually the subcategory
      const subcategoryId = selectedProduct.subcategoryId || selectedProduct.categoryId;
      const parentCategoryId = findParentCategoryId(subcategoryId);

      const result = await initialInventoryAPI.createBulkStock({
        productId: selectedProduct.productId,
        categoryId: parentCategoryId,
        subcategoryId: subcategoryId,
        quantity,
        location,
        subLocation,
        lotNumber,
        expirationDate,
        acquisitionNotes: notes,
        acquisitionSource: 'INITIAL_INVENTORY'
      });

      pageLogger.info('Bulk stock created', { 
        productId: selectedProduct.productId,
        quantity: result.totalQuantity,
        inventoryId: result.inventoryId
      });

      setSuccessMessage(`Successfully added ${quantity} ${selectedProduct.name} to inventory.`);

      // Refresh session summary
      await loadSessionSummary();

    } catch (err: any) {
      pageLogger.error('Failed to create bulk stock', undefined, err);
      setError(err.message || 'Failed to add items. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearProduct = () => {
    setSelectedProduct(null);
    setSuccessMessage(null);
    setError(null);
  };

  const handleResetSession = () => {
    if (window.confirm('This will start a fresh session and clear the session summary. Continue?')) {
      localStorage.removeItem(SESSION_KEY);
      setSessionSummary(null);
      initializeSession();
    }
  };

  // Determine tracking type for selected product
  const getTrackingType = (): 'SERIALIZED' | 'BULK_TRACKED' | 'CONSUMABLE' | null => {
    if (!selectedProduct) return null;
    
    // First try trackingType from product
    if (selectedProduct.trackingType) {
      return selectedProduct.trackingType;
    }
    
    // Fall back to category lookup
    const subcategory = categories.find(c => c.categoryId === selectedProduct.subcategoryId);
    if (subcategory) {
      const config = getItemTrackingConfig(subcategory);
      return config.trackingType;
    }
    
    // Last resort: use productType
    return selectedProduct.productType === 'SERIALIZED' ? 'SERIALIZED' : 'BULK_TRACKED';
  };

  const trackingType = getTrackingType();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Initial Inventory Setup</h1>
        <p className="text-gray-600">
          Add existing inventory to the system. Select a product from your catalog, then enter the items.
          No purchase order required.
        </p>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Entry Form (2/3 width) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Step 1: Product Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm mr-2">1</span>
                Select or Create Product
              </h2>
              {selectedProduct && (
                <button
                  onClick={handleClearProduct}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Change Product
                </button>
              )}
            </div>

            <ProductSearchCreate
              products={products}
              categories={categories}
              selectedProduct={selectedProduct}
              onProductSelected={handleProductSelected}
              onCreateProduct={handleCreateProduct}
            />
          </div>

          {/* Step 2: Inventory Entry (conditional based on tracking type) */}
          {selectedProduct && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
                <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm mr-2">2</span>
                Add Inventory
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({selectedProduct.name})
                </span>
              </h2>

              {/* Success/Error Messages */}
              {successMessage && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-green-800">{successMessage}</p>
                    <p className="text-sm text-green-600 mt-1">
                      Add more items below or select a different product.
                    </p>
                  </div>
                </div>
              )}

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                  <p className="text-red-800">{error}</p>
                </div>
              )}

              {/* Entry Form Based on Tracking Type */}
              {trackingType === 'SERIALIZED' ? (
                <SerializedItemEntry
                  product={selectedProduct}
                  categories={categories}
                  onSubmit={handleSerializedSubmit}
                  submitting={submitting}
                />
              ) : (
                <BulkQuantityEntry
                  product={selectedProduct}
                  categories={categories}
                  trackingType={trackingType || 'BULK_TRACKED'}
                  onSubmit={handleBulkSubmit}
                  submitting={submitting}
                />
              )}
            </div>
          )}

          {/* Empty State - No product selected */}
          {!selectedProduct && (
            <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Product to Continue</h3>
              <p className="text-gray-500 mb-4">
                Search for an existing product in your catalog or create a new one.
              </p>
              <button
                onClick={handleCreateProduct}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New Product
              </button>
            </div>
          )}
        </div>

        {/* Right Column - Session Summary (1/3 width) */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6 sticky top-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">This Session</h2>
              <button
                onClick={handleResetSession}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center"
                title="Reset session"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Reset
              </button>
            </div>

            <SessionSummary
              summary={sessionSummary}
              categories={categories}
              onRefresh={loadSessionSummary}
            />
          </div>
        </div>
      </div>

      {/* Add Product Modal */}
      {showAddProductModal && (
        <AddProductModal
          onClose={() => setShowAddProductModal(false)}
          onProductCreated={handleProductCreated}
          categories={categories}
        />
      )}
    </div>
  );
};

export default InitialInventoryPage;